import { Cpu, HardDrive, MemoryStick } from 'lucide-react'
import type { LucideIcon, LucideProps } from 'lucide-react'
import type { CSSProperties } from 'react'
import type { CatalogCapacityDto } from '../../data/types'
import { formatCpuCores, formatMemory } from './hostLabels'

type CapabilityModel = string | null | undefined

export interface CapabilityModels {
  cpu?: CapabilityModel
  memory?: CapabilityModel
  storage?: CapabilityModel
  gpu?: CapabilityModel
}

function amountOrDash(value: string, unavailable: string): string {
  return value === unavailable ? '—' : value
}

function cpuAmount(capacity: CatalogCapacityDto | null): string {
  if (!capacity) return '—'
  return amountOrDash(formatCpuCores(capacity.cpuMilli).replace(/ cores$/, ''), 'CPU not reported')
}

function memoryAmount(bytes: number | null): string {
  return amountOrDash(formatMemory(bytes), 'Not reported')
}

function gpuAmount(capacity: CatalogCapacityDto | null): string {
  if (!capacity) return '—'
  if (capacity.gpuUnits !== null && Number.isFinite(capacity.gpuUnits) && capacity.gpuUnits >= 0) return String(capacity.gpuUnits)
  return '—'
}

function modelText(model: CapabilityModel): string {
  const value = typeof model === 'string' ? model.trim() : ''
  return value || '—'
}

function GpuIcon({ className, size = 20, 'aria-hidden': ariaHidden }: LucideProps) {
  const source = `${import.meta.env.BASE_URL}assets/gpu.png`
  return <span
    className={`${className ?? ''} catalog-capability-icon-gpu`.trim()}
    style={{ '--gpu-icon-url': `url(${source})`, '--gpu-icon-size': `${size}px` } as CSSProperties}
    aria-hidden={ariaHidden}
  />
}

export const CAPABILITY_ROWS: readonly { id: keyof CapabilityModels; label: string; Icon: LucideIcon | typeof GpuIcon }[] = [
  { id: 'cpu', label: 'CPU', Icon: Cpu },
  { id: 'memory', label: 'Memory', Icon: MemoryStick },
  { id: 'storage', label: 'Storage', Icon: HardDrive },
  { id: 'gpu', label: 'GPU', Icon: GpuIcon },
]

export function CatalogCapabilities({ capacity, models: given = {} }: { capacity: CatalogCapacityDto | null; models?: CapabilityModels }) {
  // The host already reports its CPU and GPU models with its capacity; an
  // explicit model still wins so a caller can override what the host said.
  const models: CapabilityModels = { ...given, cpu: given.cpu ?? capacity?.cpu, gpu: given.gpu ?? capacity?.gpu }
  const amounts: Record<keyof CapabilityModels, string> = {
    cpu: cpuAmount(capacity),
    memory: memoryAmount(capacity?.memoryBytes ?? null),
    storage: memoryAmount(capacity?.storageBytes ?? null),
    gpu: gpuAmount(capacity),
  }

  return <div className="catalog-capabilities" aria-label="Capabilities">
    {CAPABILITY_ROWS.map(({ id, label, Icon }) => <div key={id} className="catalog-capability" role="group" aria-label={label}>
      <Icon className="catalog-capability-icon" size={20} strokeWidth={1.8} aria-hidden="true" />
      <strong className="catalog-capability-amount">{amounts[id]}</strong>
      <span className={`catalog-capability-model${models[id] ? '' : ' catalog-capability-model-placeholder'}`} title={models[id] ? modelText(models[id]) : 'Model not reported'}>{modelText(models[id])}</span>
    </div>)}
  </div>
}
